import Icon from "../Icon";
import _first from "lodash/first";
import _last from "lodash/last";
import _partition from "lodash/partition";
import PropTypes from "prop-types";
import qs from "query-string";
import React from "react";
import { Link } from "react-router-dom";

import { AUDIENCES, endash } from "./audienceProps";
import Button from "../Button";
import MiniPage from "../MiniPage";
import { DEVICES } from "../../modules/devices/constants";
import { getLabelForPage } from "../../modules/pages/selectors";
import { ConfigureProjectEvents } from "../../modules/tracker/event-names";
import { makeClassNameFunctionForModule } from "../../utils/cssModules";

import styles from "../../styles/createTest/ReviewTest.css";

const classNames = makeClassNameFunctionForModule(styles);

const imagePropTypes = PropTypes.shape({
    files: PropTypes.shape({
        original: PropTypes.shape({
            imageUrl: PropTypes.string.isRequired
        }).isRequired
    }).isRequired
});

export class StepSummary extends React.PureComponent {
    static propTypes = {
        isValid: PropTypes.bool.isRequired,
        labelText: PropTypes.node.isRequired,
        labelError: PropTypes.node,
        linkQuery: PropTypes.object.isRequired,
        children: PropTypes.node.isRequired,
        isReadOnly: PropTypes.bool.isRequired
    };

    render() {
        const error = !this.props.isValid &&
            this.props.labelError && (
                <p className={styles.labelError}>{this.props.labelError}</p>
            );

        const queryString = `?${qs.stringify(this.props.linkQuery)}`;

        return (
            <div className={styles.stepSummary}>
                <Icon
                    {...classNames(
                        "icon",
                        this.props.isValid ? "valid" : "invalid"
                    )}
                    icon={
                        this.props.isValid
                            ? "check-circle"
                            : "exclamation-triangle"
                    }
                />

                <div className={styles.stepLabel}>
                    <p className={styles.labelText}>{this.props.labelText}</p>
                    {error}
                </div>
                <div className={styles.summaryContent}>
                    {this.props.children}
                </div>
                {!this.props.isReadOnly && (
                    <div className={styles.summaryActions}>
                        <Link
                            to={{ search: queryString }}
                            {...classNames(
                                "action",
                                this.props.isValid ? "valid" : "invalid"
                            )}
                        >
                            <Icon icon="pencil-alt" />{" "}
                            {this.props.isValid ? "Edit" : "Resolve"}
                        </Link>
                    </div>
                )}
            </div>
        );
    }
}

export class DefinitionSummary extends React.PureComponent {
    static propTypes = {
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        devices: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                iconImage: imagePropTypes.isRequired
            })
        ).isRequired,
        isReadOnly: PropTypes.bool.isRequired
    };

    render() {
        return (
            <StepSummary
                labelText="Define Test"
                linkQuery={{ step: 1 }}
                isValid={true}
                isReadOnly={this.props.isReadOnly}
            >
                <div className={styles.definitionSummary}>
                    <p className={styles.title}>{this.props.name}</p>
                    <div className={styles.devices}>
                        {this.props.devices.map(d => (
                            <p key={d.id} className={styles.device}>
                                {React.createElement(DEVICES[d.id].icon, {
                                    className: styles.deviceIcon
                                })}
                                />
                                {d.name}
                            </p>
                        ))}
                    </div>
                    <p className={styles.description}>
                        {this.props.description}
                    </p>
                </div>
            </StepSummary>
        );
    }
}

export class DiscreteAudienceSummary extends React.PureComponent {
    static propTypes = {
        segments: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                icon: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
                percentage: PropTypes.number.isRequired
            })
        )
    };

    render() {
        return (
            <div className={styles.discreteQuotas}>
                {this.props.segments.map(segment => (
                    <p key={segment.id} className={styles.discreteSegment}>
                        {React.createElement(segment.icon, {
                            className: styles.segmentIcon
                        })}
                        />
                        <span className={styles.segmentLabel}>
                            {segment.label}:{" "}
                        </span>
                        <span className={styles.segmentQuota}>
                            {segment.percentage}%
                        </span>
                    </p>
                ))}
            </div>
        );
    }
}

export class ContinuousAudienceSummary extends React.PureComponent {
    static propTypes = {
        icon: PropTypes.string.isRequired,
        isValid: PropTypes.bool.isRequired,
        title: PropTypes.string.isRequired,
        total: PropTypes.number.isRequired,
        segments: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                label: PropTypes.string.isRequired,
                percentage: PropTypes.number.isRequired
            })
        )
    };

    render() {
        return (
            <div className={styles.continuousQuotas}>
                <p className={styles.fullRange}>
                    {React.createElement(this.props.icon, {
                        className: styles.audienceIcon
                    })}
                    />
                    {this.props.title}
                </p>
                <table className={styles.quotasTable}>
                    <tbody>
                        {this.props.segments.map(segment => (
                            <tr key={segment.id}>
                                <td className={styles.segmentRange}>
                                    {segment.label}:
                                </td>
                                <td className={styles.segmentQuota}>
                                    {segment.percentage}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {this.props.isValid || (
                        <tfoot>
                            <tr>
                                <td {...classNames("segmentRange", "invalid")}>
                                    Total:
                                </td>
                                <td {...classNames("segmentQuota", "invalid")}>
                                    {this.props.total}%
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        );
    }
}

export class AudienceSummary extends React.PureComponent {
    static propTypes = {
        isValid: PropTypes.bool.isRequired,
        labelError: PropTypes.node,
        audienceDescription: PropTypes.string,
        audiences: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                icon: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.arrayOf(PropTypes.string)
                ]).isRequired,
                isValid: PropTypes.bool.isRequired,
                title: PropTypes.string.isRequired,
                total: PropTypes.number.isRequired,
                type: PropTypes.oneOf(["continuous", "discrete"]).isRequired,
                segments: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.number.isRequired,
                        icon: PropTypes.string,
                        label: PropTypes.string.isRequired,
                        percentage: PropTypes.number.isRequired
                    })
                ).isRequired
            })
        ).isRequired,
        isReadOnly: PropTypes.bool.isRequired
    };

    render() {
        const [discreteAudiences, continuousAudiences] = _partition(
            this.props.audiences,
            audience => audience.type === "discrete"
        );

        return (
            <StepSummary
                labelText="Audience"
                linkQuery={{ step: 2 }}
                isValid={this.props.isValid}
                labelError={this.props.labelError}
                isReadOnly={this.props.isReadOnly}
            >
                <div className={styles.audienceSummary}>
                    {this.props.audiences.length === 0 && (
                        <p className={styles.noSelection}>
                            No audiences selected.
                        </p>
                    )}
                    {this.props.audiences.length > 0 &&
                        discreteAudiences.map(audience => (
                            <DiscreteAudienceSummary
                                key={audience.id}
                                segments={audience.segments}
                            />
                        ))}
                    {this.props.audiences.length > 0 && (
                        <div className={styles.continuousAudienceSummaries}>
                            {continuousAudiences.map(audience => (
                                <ContinuousAudienceSummary
                                    key={audience.id}
                                    icon={audience.icon}
                                    isValid={audience.isValid}
                                    title={audience.title}
                                    total={audience.total}
                                    segments={audience.segments}
                                />
                            ))}
                        </div>
                    )}
                    {this.props.audienceDescription && (
                        <div className={styles.audienceDescription}>
                            <p className={styles.header}>
                                <Icon
                                    className={styles.headerIcon}
                                    icon="cog"
                                />
                                Custom Requests
                            </p>
                            <p className={styles.description}>
                                {this.props.audienceDescription}
                            </p>
                        </div>
                    )}
                </div>
            </StepSummary>
        );
    }
}

export class PagesSummary extends React.PureComponent {
    static propTypes = {
        isReadOnly: PropTypes.bool.isRequired,
        isValid: PropTypes.bool.isRequired,
        labelError: PropTypes.node,
        pages: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                image: imagePropTypes.isRequired,
                isValid: PropTypes.bool.isRequired,
                numCtas: PropTypes.number.isRequired
            })
        ).isRequired
    };

    render() {
        const { className: linkClassName } = classNames(
            "miniPage",
            this.props.isReadOnly && "readOnly"
        );
        return (
            <StepSummary
                labelText="Pages & CTAs"
                linkQuery={{ step: 3 }}
                isValid={this.props.isValid}
                labelError={this.props.labelError}
                isReadOnly={this.props.isReadOnly}
            >
                <div className={styles.pagesSummary}>
                    {this.props.pages.length === 0 ? (
                        <p className={styles.noSelection}>No pages selected.</p>
                    ) : (
                        this.props.pages.map(
                            ({ isValid, numCtas, ...page }) => (
                                <Link
                                    key={page.id}
                                    to={{
                                        search:
                                            "?" +
                                            qs.stringify({
                                                step: 4,
                                                page: page.id
                                            })
                                    }}
                                    className={linkClassName}
                                >
                                    <MiniPage
                                        page={page}
                                        isValid={isValid}
                                        numCtas={numCtas}
                                        renderOverlay={
                                            this.props.isReadOnly
                                                ? null
                                                : () => (
                                                      <div
                                                          className={
                                                              styles.pageImageOverlay
                                                          }
                                                      >
                                                          <p
                                                              className={
                                                                  styles.numCtas
                                                              }
                                                          >
                                                              {numCtas} CTAs
                                                          </p>
                                                          <p
                                                              className={
                                                                  styles.editCtas
                                                              }
                                                          >
                                                              <Icon icon="pencil" />{" "}
                                                              edit
                                                          </p>
                                                      </div>
                                                  )
                                        }
                                    />
                                </Link>
                            )
                        )
                    )}
                </div>
            </StepSummary>
        );
    }
}

export default class ReviewTest extends React.PureComponent {
    constructor(props) {
        super(props);
        this.saveAndExit = this.saveAndExit.bind(this);
        this.launchTest = this.launchTest.bind(this);
    }

    componentDidMount() {
        this.props.trackEvent(
            ConfigureProjectEvents.DID_NAVIGATE_TO_PROJECT_REVIEW
        );
    }

    launchTest() {
        this.props.launchWevo(
            this.props.wevo,
            `/team/${this.props.wevo.ownerTeamId}/dashboard`
        );
    }

    saveAndExit() {
        this.props.trackEvent(
            ConfigureProjectEvents.DID_CLICK_REVIEW_SAVE_AND_EXIT
        );
        this.props.navigateTo(`/team/${this.props.wevo.ownerTeamId}/dashboard`);
    }

    render() {
        const pages = this.props.wevo.pages
            .filter(page => page.isActive)
            .sort((p1, p2) => p1.sortOrder - p2.sortOrder)
            .map((page, index) => {
                const activeCtas = page.ctas.filter(cta => cta.isActive);
                const numCtas = activeCtas.length;
                return {
                    id: page.id,
                    name: page.name,
                    pageLabel: getLabelForPage(index),
                    image: page.image,
                    isValid: numCtas > 0,
                    numCtas
                };
            });
        const hasPages = pages.length > 0;
        const areAllPagesValid = pages.every(page => page.isValid);
        const pagesLabelError = areAllPagesValid
            ? null
            : "All pages must have CTAs.";

        // The Add Audience screen doesn't let you unselect every segment of an active audience, so I shouldn't really
        // have to pass a 2nd argument to reduce here. Of course, the data model does allow you do have an audience
        // with 0 selected segments and it's easy enough to handle.
        const sumSelectedSegments = audience =>
            audience.segments
                .filter(segment => segment.isSelected)
                .map(segment => segment.percentage)
                .reduce((sum, pct) => sum + pct, 0);

        const audiences = this.props.wevo.audiences
            .filter(audience => audience.isActive)
            .map(audience => {
                const audienceInfoIndex = AUDIENCES.findIndex(
                    info => info.id === audience.id
                );
                const audienceInfo = AUDIENCES[audienceInfoIndex];

                const total = sumSelectedSegments(audience);

                const segments = audience.segments
                    .filter(segment => segment.isSelected)
                    .sort(
                        (s1, s2) => s1.segment.sortOrder - s2.segment.sortOrder
                    )
                    .map(segment => {
                        const info = audienceInfo.segments.find(
                            info => info.id === segment.segment.id
                        );
                        return {
                            id: segment.segment.id,
                            icon: info.icon,
                            label: info.label,
                            percentage: segment.percentage,
                            upperBound: info.upperBound || info.label,
                            lowerBound: info.lowerBound || info.label
                        };
                    });

                const firstSegment = _first(segments);
                const lastSegment = _last(segments);

                // firstSegment & lastSegment will be null if the audience has no selected segments. It's not clear
                // what
                // we should display in this case. I'm choosing the audience title arbitrarily. We should not see this
                // case in real life (see comment on sumSelectedSegments).
                const title =
                    firstSegment && lastSegment
                        ? `${firstSegment.lowerBound}${endash}${
                              lastSegment.upperBound
                          } ${audienceInfo.units}`
                        : audienceInfo.title;

                return {
                    id: audience.id,
                    icon: audienceInfo.icon,
                    isValid: total === 100,
                    title,
                    total,
                    type: audienceInfo.type,
                    segments: segments,
                    sortOrder: audienceInfoIndex
                };
            })
            .sort((a, b) => a.sortOrder - b.sortOrder);
        const hasAudiences = audiences.length > 0;
        const areAllAudiencesValid = audiences.every(
            audience => audience.isValid
        );
        const audiencesLabelError = areAllAudiencesValid
            ? null
            : "Quotas must total 100%.";
        const audienceDescription = this.props.wevo.audienceDescription;

        const isDraft = this.props.wevo.status === "draft";

        const canSubmit =
            isDraft &&
            hasPages &&
            areAllPagesValid &&
            hasAudiences &&
            areAllAudiencesValid;
        return (
            <div className={styles.chapter}>
                <div className={styles.chapterHeader}>
                    <h2 className={styles.chapterTitle}>Review Test</h2>
                </div>
                <div className={styles.reviewTest}>
                    <DefinitionSummary
                        {...this.props.wevo}
                        isReadOnly={this.props.isReadOnly}
                    />

                    <AudienceSummary
                        audiences={audiences}
                        isValid={hasAudiences && areAllAudiencesValid}
                        labelError={audiencesLabelError}
                        isReadOnly={this.props.isReadOnly}
                        audienceDescription={audienceDescription}
                    />

                    <PagesSummary
                        pages={pages}
                        isValid={hasPages && areAllPagesValid}
                        labelError={pagesLabelError}
                        isReadOnly={this.props.isReadOnly}
                    />
                    {!this.props.isReadOnly && (
                        <div className={styles.buttons}>
                            <Button
                                className={styles.saveAndExit}
                                color="pale"
                                onClick={this.saveAndExit}
                            >
                                Save & Exit
                            </Button>
                            <Button
                                className={styles.submitForReview}
                                color="success"
                                disabled={!canSubmit}
                                onClick={this.launchTest}
                            >
                                Submit Test
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/CreateTest/ReviewTest.js